import React, { useEffect, useMemo, useState } from "react";
import { baseMenuItems } from "../../data/menuData";
import { getMenuItemWithRecipe } from "../../utils/menuHelpers";
import { getSupabase } from "../../lib/supabase";

type RowState = "clean" | "dirty" | "saving" | "saved" | "error";

interface PriceRow {
  itemId: string;
  name: string;
  submenu: string;
  location: string;
  base: number;
  override: number | null;
  draft: string;
  state: RowState;
  message?: string;
}

function buildRows(overrides: Map<string, number>): PriceRow[] {
  return baseMenuItems.map((item) => {
    const enriched = getMenuItemWithRecipe(item);
    const override = overrides.get(item.id);
    const effective = override ?? item.price;
    return {
      itemId: item.id,
      name: enriched.name || item.id,
      submenu: item.submenu ?? "",
      location: item.location,
      base: item.price,
      override: override ?? null,
      draft: effective.toFixed(2),
      state: "clean",
    };
  });
}

const StatusPill: React.FC<{ row: PriceRow }> = ({ row }) => {
  const { state, override, message } = row;
  if (state === "saving")  return <span className="admin-status is-saving"><span className="dot" />Saving</span>;
  if (state === "saved")   return <span className="admin-status is-saved"><span className="dot" />Saved</span>;
  if (state === "error")   return <span className="admin-status is-error" title={message}><span className="dot" />Error</span>;
  if (state === "dirty")   return <span className="admin-status is-dirty"><span className="dot" />Unsaved</span>;
  if (override !== null)   return <span className="admin-status is-live"><span className="dot" />Live</span>;
  return <span className="admin-status is-base"><span className="dot" />Base</span>;
};

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const LivePricesManager: React.FC = () => {
  const supabase = useMemo(() => getSupabase(), []);
  const [rows, setRows] = useState<PriceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filterLocation, setFilterLocation] = useState("all");
  const [filterSubmenu, setFilterSubmenu] = useState("all");
  const [showOnlyOverridden, setShowOnlyOverridden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("menu_item_prices")
        .select("item_id, price");
      if (cancelled) return;
      if (error) {
        setLoadError(error.message);
        setRows(buildRows(new Map()));
      } else {
        const map = new Map<string, number>();
        for (const r of data ?? []) map.set(r.item_id, Number(r.price));
        setRows(buildRows(map));
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [supabase]);

  const submenus = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) if (r.submenu) set.add(r.submenu);
    return Array.from(set).sort();
  }, [rows]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filterLocation !== "all" && r.location !== filterLocation && r.location !== "both") return false;
      if (filterSubmenu !== "all" && r.submenu !== filterSubmenu) return false;
      if (showOnlyOverridden && r.override === null) return false;
      if (q && !r.name.toLowerCase().includes(q) && !r.itemId.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, search, filterLocation, filterSubmenu, showOnlyOverridden]);

  const overrideCount = useMemo(() => rows.filter((r) => r.override !== null).length, [rows]);
  const dirtyRows = useMemo(() => rows.filter((r) => r.state === "dirty"), [rows]);
  const dirtyCount = dirtyRows.length;
  const [bulkSaving, setBulkSaving] = useState(false);

  const updateRow = (itemId: string, patch: Partial<PriceRow>) => {
    setRows((prev) => prev.map((r) => (r.itemId === itemId ? { ...r, ...patch } : r)));
  };

  const updateMany = (updates: Map<string, Partial<PriceRow>>) => {
    setRows((prev) => prev.map((r) => (updates.has(r.itemId) ? { ...r, ...updates.get(r.itemId)! } : r)));
  };

  const handleChange = (itemId: string, value: string) => {
    updateRow(itemId, { draft: value, state: "dirty", message: undefined });
  };

  const handleSave = async (row: PriceRow) => {
    const parsed = Number(row.draft);
    if (!Number.isFinite(parsed) || parsed < 0) {
      updateRow(row.itemId, { state: "error", message: "Invalid price" });
      return;
    }
    updateRow(row.itemId, { state: "saving", message: undefined });
    const { error } = await supabase
      .from("menu_item_prices")
      .upsert({ item_id: row.itemId, price: parsed }, { onConflict: "item_id" });
    if (error) {
      updateRow(row.itemId, { state: "error", message: error.message });
    } else {
      updateRow(row.itemId, {
        state: "saved",
        override: parsed,
        draft: parsed.toFixed(2),
        message: undefined,
      });
      window.setTimeout(() => updateRow(row.itemId, { state: "clean" }), 1500);
    }
  };

  const handleSaveAll = async () => {
    if (dirtyRows.length === 0 || bulkSaving) return;

    // Validate and partition into valid/invalid
    const valid: { itemId: string; price: number }[] = [];
    const invalid = new Map<string, Partial<PriceRow>>();
    for (const r of dirtyRows) {
      const parsed = Number(r.draft);
      if (!Number.isFinite(parsed) || parsed < 0) {
        invalid.set(r.itemId, { state: "error", message: "Invalid price" });
      } else {
        valid.push({ itemId: r.itemId, price: parsed });
      }
    }

    if (invalid.size > 0) updateMany(invalid);
    if (valid.length === 0) return;

    setBulkSaving(true);

    // Mark all valid rows as saving
    const savingPatches = new Map<string, Partial<PriceRow>>();
    for (const v of valid) savingPatches.set(v.itemId, { state: "saving", message: undefined });
    updateMany(savingPatches);

    const { error } = await supabase
      .from("menu_item_prices")
      .upsert(
        valid.map((v) => ({ item_id: v.itemId, price: v.price })),
        { onConflict: "item_id" },
      );

    if (error) {
      const errPatches = new Map<string, Partial<PriceRow>>();
      for (const v of valid) errPatches.set(v.itemId, { state: "error", message: error.message });
      updateMany(errPatches);
    } else {
      const savedPatches = new Map<string, Partial<PriceRow>>();
      for (const v of valid) {
        savedPatches.set(v.itemId, {
          state: "saved",
          override: v.price,
          draft: v.price.toFixed(2),
          message: undefined,
        });
      }
      updateMany(savedPatches);
      // Settle back to clean after the flash animation
      window.setTimeout(() => {
        const clearPatches = new Map<string, Partial<PriceRow>>();
        for (const v of valid) clearPatches.set(v.itemId, { state: "clean" });
        updateMany(clearPatches);
      }, 1500);
    }

    setBulkSaving(false);
  };

  const handleRevertAll = async () => {
    if (dirtyRows.length === 0 || bulkSaving) return;

    // Partition: rows with an existing override need a DB delete;
    // rows that are just dirty drafts only need a local reset.
    const toDelete = dirtyRows.filter((r) => r.override !== null);
    const draftOnly = dirtyRows.filter((r) => r.override === null);

    if (toDelete.length > 0) {
      const ok = window.confirm(
        `Revert all ${dirtyRows.length} unsaved changes?\n\n` +
          `${toDelete.length} of these will also delete the live override in Supabase, ` +
          `falling those items back to their base prices.`,
      );
      if (!ok) return;
    }

    setBulkSaving(true);

    // Local-only resets (drafts with no override)
    if (draftOnly.length > 0) {
      const patches = new Map<string, Partial<PriceRow>>();
      for (const r of draftOnly) {
        patches.set(r.itemId, { state: "clean", draft: r.base.toFixed(2), message: undefined });
      }
      updateMany(patches);
    }

    // DB deletes for rows with overrides
    if (toDelete.length > 0) {
      const savingPatches = new Map<string, Partial<PriceRow>>();
      for (const r of toDelete) savingPatches.set(r.itemId, { state: "saving", message: undefined });
      updateMany(savingPatches);

      const { error } = await supabase
        .from("menu_item_prices")
        .delete()
        .in(
          "item_id",
          toDelete.map((r) => r.itemId),
        );

      if (error) {
        const errPatches = new Map<string, Partial<PriceRow>>();
        for (const r of toDelete) errPatches.set(r.itemId, { state: "error", message: error.message });
        updateMany(errPatches);
      } else {
        const savedPatches = new Map<string, Partial<PriceRow>>();
        for (const r of toDelete) {
          savedPatches.set(r.itemId, {
            state: "saved",
            override: null,
            draft: r.base.toFixed(2),
            message: undefined,
          });
        }
        updateMany(savedPatches);
        window.setTimeout(() => {
          const clearPatches = new Map<string, Partial<PriceRow>>();
          for (const r of toDelete) clearPatches.set(r.itemId, { state: "clean" });
          updateMany(clearPatches);
        }, 1500);
      }
    }

    setBulkSaving(false);
  };

  const handleRevert = async (row: PriceRow) => {
    if (row.override === null) {
      updateRow(row.itemId, { draft: row.base.toFixed(2), state: "clean", message: undefined });
      return;
    }
    updateRow(row.itemId, { state: "saving", message: undefined });
    const { error } = await supabase
      .from("menu_item_prices")
      .delete()
      .eq("item_id", row.itemId);
    if (error) {
      updateRow(row.itemId, { state: "error", message: error.message });
    } else {
      updateRow(row.itemId, {
        state: "saved",
        override: null,
        draft: row.base.toFixed(2),
        message: undefined,
      });
      window.setTimeout(() => updateRow(row.itemId, { state: "clean" }), 1500);
    }
  };

  return (
    <div>
      <div className="admin-page-intro">
        <div className="admin-page-intro-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        </div>
        <div className="admin-page-intro-body">
          <strong>Edits persist to Supabase immediately.</strong> The public site rebuilds via the
          Render Deploy Hook and serves new prices within ~2 minutes. Locally,
          run <code>npm&nbsp;run&nbsp;sync-menu</code> to see changes hot-reload.
        </div>
      </div>

      {loadError && (
        <div className="admin-error-banner">
          Could not load prices: {loadError}. You can still edit; saves will retry against Supabase.
        </div>
      )}

      {dirtyCount > 0 && (
        <div className="admin-pending-bar" role="region" aria-label="Pending changes">
          <div className="admin-pending-bar-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
              <polyline points="17 21 17 13 7 13 7 21" />
              <polyline points="7 3 7 8 15 8" />
            </svg>
          </div>
          <div className="admin-pending-bar-body">
            <div className="admin-pending-bar-title">
              {dirtyCount} unsaved {dirtyCount === 1 ? "change" : "changes"}
            </div>
            <div className="admin-pending-bar-sub">
              Save all to push to Supabase, or revert to roll back (and drop overrides).
            </div>
          </div>
          <div className="admin-pending-bar-actions">
            <button
              className="admin-btn admin-btn-on-dark"
              onClick={handleRevertAll}
              disabled={bulkSaving}
            >
              Revert all
            </button>
            <button
              className="admin-btn admin-btn-gold"
              onClick={handleSaveAll}
              disabled={bulkSaving}
            >
              {bulkSaving ? (
                <><span className="admin-spinner" />Saving {dirtyCount}…</>
              ) : (
                <>Save all ({dirtyCount})</>
              )}
            </button>
          </div>
        </div>
      )}

      <div className="admin-filter-bar">
        <div className="admin-search">
          <SearchIcon />
          <input
            type="search"
            placeholder="Search by name or ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select className="admin-select" value={filterLocation} onChange={(e) => setFilterLocation(e.target.value)}>
          <option value="all">All locations</option>
          <option value="montreal">Montreal</option>
          <option value="delson">Delson</option>
        </select>
        <select className="admin-select" value={filterSubmenu} onChange={(e) => setFilterSubmenu(e.target.value)}>
          <option value="all">All submenus</option>
          {submenus.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <label className="admin-toggle">
          <input
            type="checkbox"
            checked={showOnlyOverridden}
            onChange={(e) => setShowOnlyOverridden(e.target.checked)}
          />
          Only overridden ({overrideCount})
        </label>
        <div className="admin-result-count">
          <strong>{visibleRows.length}</strong> of {rows.length} items
        </div>
      </div>

      <div className="admin-data-card">
        {loading ? (
          <div className="admin-empty">
            <div className="admin-empty-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="2" x2="12" y2="6" /><line x1="12" y1="18" x2="12" y2="22" />
                <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" /><line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
                <line x1="2" y1="12" x2="6" y2="12" /><line x1="18" y1="12" x2="22" y2="12" />
                <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" /><line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
              </svg>
            </div>
            <div className="admin-empty-title">Loading prices…</div>
            <div>Fetching the latest from Supabase.</div>
          </div>
        ) : visibleRows.length === 0 ? (
          <div className="admin-empty">
            <div className="admin-empty-icon">
              <SearchIcon />
            </div>
            <div className="admin-empty-title">No matches</div>
            <div>Try clearing filters or changing your search.</div>
          </div>
        ) : (
          <table className="admin-data-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Location</th>
                <th>Submenu</th>
                <th style={{ textAlign: "right" }}>Base</th>
                <th style={{ textAlign: "right" }}>Live price</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => {
                const flashClass =
                  row.state === "saved" ? "admin-flash-saved"
                  : row.state === "error" ? "admin-flash-error"
                  : "";
                const inputFlashClass =
                  row.state === "saved" ? "is-flash-saved"
                  : row.state === "error" ? "is-flash-error"
                  : "";
                return (
                  <tr key={row.itemId} className={flashClass}>
                    <td className="admin-item-cell">
                      <div className="name">{row.name}</div>
                      <div className="id">{row.itemId}</div>
                    </td>
                    <td data-label="Location"><span className="admin-meta-tag">{row.location}</span></td>
                    <td data-label="Submenu"><span className="admin-meta-tag">{row.submenu || "—"}</span></td>
                    <td className="admin-base-price" data-label="Base" style={{ textAlign: "right" }}>
                      ${row.base.toFixed(2)}
                    </td>
                    <td data-label="Live price" style={{ textAlign: "right" }}>
                      {(() => {
                        const max = Math.max(Math.ceil(row.base * 2), 10);
                        const draftNum = Number(row.draft);
                        const fillPct =
                          Number.isFinite(draftNum) && max > 0
                            ? Math.min(100, Math.max(0, (draftNum / max) * 100))
                            : 0;
                        return (
                          <div className="admin-price-slider-wrap">
                            <input
                              className={`admin-price-input ${inputFlashClass}`}
                              type="number"
                              step="0.01"
                              min="0"
                              value={row.draft}
                              onChange={(e) => handleChange(row.itemId, e.target.value)}
                              disabled={row.state === "saving"}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleSave(row);
                              }}
                            />
                            <input
                              className="admin-price-slider"
                              type="range"
                              min={0}
                              max={max}
                              step={0.05}
                              value={Number.isFinite(draftNum) ? draftNum : 0}
                              onChange={(e) => handleChange(row.itemId, Number(e.target.value).toFixed(2))}
                              disabled={row.state === "saving"}
                              aria-label={`Adjust price for ${row.name}`}
                              style={{ ["--admin-slider-fill" as never]: `${fillPct}%` }}
                            />
                            <div className="admin-price-slider-range">
                              <span>$0</span>
                              <span>${max}</span>
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td data-label="Status" aria-live="polite"><StatusPill row={row} /></td>
                    <td>
                      <div className="admin-row-actions">
                        <button
                          className="admin-btn admin-btn-primary"
                          onClick={() => handleSave(row)}
                          disabled={row.state === "saving" || row.state === "clean"}
                        >
                          {row.state === "saving" ? <><span className="admin-spinner" />Saving</> : "Save"}
                        </button>
                        <button
                          className="admin-btn admin-btn-ghost"
                          onClick={() => handleRevert(row)}
                          disabled={row.state === "saving" || (row.override === null && row.state === "clean")}
                          title={row.override !== null ? "Remove override, fall back to base price" : "Reset draft"}
                        >
                          Revert
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default LivePricesManager;
